import type { GeoDirectionalLine } from "@/shared/charts";
import {
  worldToFloorplanPixel,
  type WorldToPixelTransformAsset,
} from "@/features/deployment-dashboard/widgets/panels/facility-schematic/services/world-to-pixel-transform";
import type { ThroughputLineOption } from "@/features/deployment-dashboard/widgets/charts/throughput-trend/types";
import { getThroughputLineDisplayName } from "@/features/deployment-dashboard/widgets/charts/throughput-trend/throughput-trend-chart.utils";

/** Stable ECharts map name for one deployment area's throughput floor plan. */
export function getThroughputLineMapName(deploymentId: string, areaId: string): string {
  return `throughput-line-map:${deploymentId}:${areaId}`;
}

export interface ThroughputLinePixelGeometry {
  start: [number, number];
  end: [number, number];
  positiveReferencePoint: [number, number];
}

const THROUGHPUT_LINE_INDEX_BY_KEY: ReadonlyMap<string, number> = new Map([
  ["securityexit", 1],
  ["idlgroundfloorentrance", 1],
  ["dutyfreeentrance", 2],
  ["gates3043exit", 3],
  ["gates128exit", 4],
]);

/** Lines whose configured positive and negative directions are reversed on the floor plan. */
const REVERSE_THROUGHPUT_LINE_DIRECTIONS: ReadonlyMap<string, boolean> = new Map([
  ["security-exit", true],
]);

function normalizeThroughputLineKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/** Returns whether a line's displayed arrows need their direction colours reversed. */
export function shouldReverseThroughputLineDirection(
  line: Pick<ThroughputLineOption, "line_id">
): boolean {
  return REVERSE_THROUGHPUT_LINE_DIRECTIONS.get(line.line_id) ?? false;
}

/** Returns the image-defined index for a known floor-plan line, or null for other lines. */
export function getThroughputLineIndex(
  line: Pick<ThroughputLineOption, "line_id" | "line_name">
): number | null {
  return (
    THROUGHPUT_LINE_INDEX_BY_KEY.get(normalizeThroughputLineKey(line.line_id)) ??
    THROUGHPUT_LINE_INDEX_BY_KEY.get(normalizeThroughputLineKey(line.line_name)) ??
    null
  );
}

/**
 * Maps a throughput line's world geometry into floor-plan pixel space.
 * `positive_normal` is a vector, not a world position, so it is first added to
 * `world_start` before the same calibrated transform is applied. Transforming
 * the vector itself would incorrectly apply the transform's translation.
 */
export function transformThroughputLineGeometry(
  geometry: ThroughputLineOption["line_geometry"],
  transform: Pick<WorldToPixelTransformAsset, "matrix">
): ThroughputLinePixelGeometry {
  const start = worldToFloorplanPixel(transform, geometry.world_start.x, geometry.world_start.y);
  const end = worldToFloorplanPixel(transform, geometry.world_end.x, geometry.world_end.y);
  const positiveReferencePoint = worldToFloorplanPixel(
    transform,
    geometry.world_start.x + geometry.positive_normal.x,
    geometry.world_start.y + geometry.positive_normal.y
  );

  return {
    start: [start.x, start.y],
    end: [end.x, end.y],
    positiveReferencePoint: [positiveReferencePoint.x, positiveReferencePoint.y],
  };
}

/**
 * Builds the shared chart primitive from one configured throughput line after
 * its geometry has been transformed into the floor plan's pixel space.
 */
export function createThroughputDirectionalLine(
  line: ThroughputLineOption,
  transform: Pick<WorldToPixelTransformAsset, "matrix">,
  colors: {
    line: string;
    positive: string;
    negative: string;
  },
  emphasis?: GeoDirectionalLine["emphasis"]
): GeoDirectionalLine {
  const geometry = transformThroughputLineGeometry(line.line_geometry, transform);
  const displayName = getThroughputLineDisplayName(line.line_name);
  const reverseDirection = shouldReverseThroughputLineDirection(line);
  const lineIndex = getThroughputLineIndex(line);
  const badgeLabel = lineIndex == null ? displayName : `(${lineIndex}) ${displayName}`;

  return {
    id: `${line.areaId}:${line.line_id}`,
    ...geometry,
    color: colors.line,
    width: 2.5,
    positiveColor: reverseDirection ? colors.negative : colors.positive,
    negativeColor: reverseDirection ? colors.positive : colors.negative,
    label: badgeLabel,
    opacity: 1,
    emphasis,
  };
}

/** Returns a shared legend label when all selected lines use the same direction name. */
export function getCommonDirectionLabel(
  lines: ThroughputLineOption[],
  direction: "positive" | "negative"
): string {
  const labels = lines.map((line) =>
    direction === "positive" ? line.positive_direction.label : line.negative_direction.label
  );
  const firstLabel = labels[0];
  if (firstLabel != null && labels.every((label) => label === firstLabel)) return firstLabel;
  return direction === "positive" ? "Positive direction" : "Negative direction";
}
